import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { listChannels, listMessages, markChannelRead } from '@/lib/api/endpoints/channels'
import { listDmChannels } from '@/lib/api/endpoints/dms'
import type { Channel } from '@/lib/api/endpoints/channels'
import type { DmChannel } from '@/lib/api/endpoints/dms'
import { useSession } from '@/features/auth/hooks/useSession'
import { HttpError } from '@/lib/http'
import { resolveWorkspaceId } from '@/lib/workspace-routing'
import { POLL_INTERVAL } from '@/lib/polling'
import { detectUnreadDeltas, hasMentionOf, sameChannelList } from './lib/notifications-utils'
import { NotificationToastStack, type ToastItem } from './NotificationToast'

// A single 404 from the channel/DM list endpoints can be a transient gateway or
// cold-start blip; only treat the feature as unprovisioned (and stop polling)
// after this many CONSECUTIVE 404s so one blip can't silence notifications for
// the whole session.
export const MAX_CONSECUTIVE_404S = 3
// http.ts sets no request timeout, so a hung list fetch would otherwise keep
// pollInFlightRef latched forever and wedge every future tick. Allow a fresh
// poll once the in-flight one has been outstanding longer than this.
export const STALE_POLL_MS = 45_000
// A DM whose unread delta persists but whose triggering message never becomes
// visible (permanently outside the limit-5 fetch window, or a listMessages
// that keeps failing, e.g. 403 on a departed DM) must not re-fetch every tick
// forever. After this many attempts the baseline advances and the toast is
// abandoned; a future unread delta starts a fresh budget.
export const MAX_DM_TOAST_ATTEMPTS = 3

interface NotificationsContextValue {
  /** True when any DM is unread or a live channel @mention toast is showing. */
  hasUnread: boolean
  /** Latest group channels from the single shared poll (null until first fetch). */
  channels: Channel[] | null
  /** Latest DM channels from the single shared poll (null until first fetch). */
  dms: DmChannel[] | null
  /**
   * Optimistically clear a channel's unread badge in the shared `channels` list
   * and persist the read server-side (rolled back on failure). Also zeroes the
   * delta baseline so the next poll doesn't re-toast the just-read channel.
   * Re-homed from SidebarRail so the single owner of channel/DM state clears the
   * rail badge. (It does NOT touch the bell dot — that is derived from DM unread
   * + live mention toasts, unchanged here.)
   */
  markChannelRead: (channelId: string) => void
}

export const NotificationsContext = createContext<NotificationsContextValue>({
  hasUnread: false,
  channels: null,
  dms: null,
  markChannelRead: () => {},
})

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { token, profile, selectedContainer } = useSession()
  const workspaceId = resolveWorkspaceId(selectedContainer)
  const userId = profile?.id ?? ''
  const displayName = profile?.name || profile?.email || ''

  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [totalUnreadDMs, setTotalUnreadDMs] = useState(0)
  // Channel/DM lists from the single shared poll, exposed via context so the
  // SidebarRail can render its inline channel/DM lists without a second poll.
  const [channels, setChannels] = useState<Channel[] | null>(null)
  const [dms, setDms] = useState<DmChannel[] | null>(null)

  // Per-list seed flags: each list baselines independently on its first
  // successful fetch, so one endpoint failing from session start can't block
  // the other list's badges/toasts for the whole session.
  const seededChannelsRef = useRef(false)
  const seededDmsRef = useRef(false)
  const prevCountsRef = useRef<Record<string, number>>({})
  // Blind/failed DM toast attempts per channel id (see MAX_DM_TOAST_ATTEMPTS).
  const dmToastAttemptsRef = useRef<Record<string, number>>({})
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Channel ids with an in-flight markChannelRead POST. A poll racing the read
  // would otherwise see the server's still-stale unread count as a delta and
  // re-toast a just-opened @mention channel; we skip those until the POST settles.
  const inFlightReadsRef = useRef<Set<string>>(new Set())
  // Guards against overlapping polls stacking on slow networks: the interval
  // fires every POLL_INTERVAL regardless of whether the last poll settled.
  const pollInFlightRef = useRef(false)
  // Wall-clock start of the in-flight poll (0 when idle). Doubles as the owner
  // token so a superseded stale poll's finally can't release a newer poll's
  // guard, and lets a hung poll be overridden after STALE_POLL_MS.
  const pollStartedAtRef = useRef(0)
  // Consecutive 404s from the channel/DM list endpoints. Latches
  // channelsUnavailableRef only at MAX_CONSECUTIVE_404S so a single transient
  // 404 can't stop polling for the session.
  const consecutive404Ref = useRef(0)
  // Set once the feature is deemed unprovisioned (MAX_CONSECUTIVE_404S 404s in a
  // row) — stops polling (instead of 404-flooding every tick) until the
  // workspace changes.
  const channelsUnavailableRef = useRef(false)
  // One-shot: lets a single poll bypass the channelsUnavailable latch. Set on
  // tab refocus so a solo→team upgrade (same workspace/token, no effect re-run)
  // can re-probe once and self-heal without new event plumbing.
  const forceReprobeRef = useRef(false)
  // Poll session epoch, bumped whenever the workspace/token effect re-runs. A
  // poll started under the previous workspace/token can settle AFTER the effect
  // has reset the refs above; without this guard its catch could latch
  // channelsUnavailableRef (permanently stopping polling for the NEW workspace)
  // and its state writes could clobber the new session's channels/dms.
  const pollEpochRef = useRef(0)

  const addToast = useCallback((item: ToastItem) => {
    setToasts((prev) =>
      prev.some((t) => t.channelId === item.channelId) ? prev : [...prev, item],
    )
  }, [])

  // poll() reads these through refs so its useCallback keys only on
  // workspaceId/token/userId — a profile-name resolution or edit must not
  // re-create poll and re-run the workspace effect below (which would wipe live
  // toasts and null the published lists mid-session).
  const displayNameRef = useRef(displayName)
  useEffect(() => {
    displayNameRef.current = displayName
  }, [displayName])
  const addToastRef = useRef(addToast)
  useEffect(() => {
    addToastRef.current = addToast
  }, [addToast])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const handleActivate = useCallback(
    (toast: ToastItem) => {
      removeToast(toast.id)
      if (workspaceId) {
        if (toast.type === 'dm') {
          if (toast.otherUserId) {
            localStorage.setItem(`xavior:lastDmUser:${workspaceId}`, toast.otherUserId)
          }
        } else {
          localStorage.setItem(`xavior:lastChannel:${workspaceId}`, toast.channelId)
        }
      }
      window.dispatchEvent(
        new CustomEvent('xavior:open-channel-module', {
          detail: { channelId: toast.channelId, isDm: toast.type === 'dm', otherUserId: toast.otherUserId },
        }),
      )
    },
    [workspaceId, removeToast],
  )

  const poll = useCallback(async () => {
    if (!workspaceId || !token || !userId) return
    // A latched (not-provisioned) poll normally short-circuits, except for a
    // one-shot refocus re-probe that may heal a solo→team upgrade.
    if (channelsUnavailableRef.current && !forceReprobeRef.current) return
    // Dedupe overlapping polls, but never let a hung fetch (http.ts sets no
    // request timeout) wedge polling for the session: allow a fresh poll once
    // the in-flight one has been outstanding longer than STALE_POLL_MS.
    if (pollInFlightRef.current && Date.now() - pollStartedAtRef.current < STALE_POLL_MS) return
    // Skip polling while the tab is backgrounded; resumes on visibilitychange.
    if (typeof document !== 'undefined' && document.hidden) return
    // Consume the one-shot re-probe now that we're committed to a poll.
    const forcedReprobe = forceReprobeRef.current
    forceReprobeRef.current = false
    const epoch = pollEpochRef.current
    const startedAt = Date.now()
    pollInFlightRef.current = true
    pollStartedAtRef.current = startedAt
    try {
      // Fetch the two lists independently (allSettled) so one endpoint's
      // transient failure doesn't drop the other's update for the tick —
      // SidebarRail deliberately decoupled these downstream (#4894).
      const [channelsResult, dmsResult] = await Promise.allSettled([
        listChannels(workspaceId, token),
        listDmChannels(workspaceId, token),
      ])
      // The workspace/token changed while this poll was in flight — every ref
      // and state slice now belongs to the new session, so drop the result.
      if (epoch !== pollEpochRef.current) return

      // A single 404 can be a transient gateway/cold-start blip. Only treat the
      // feature as unprovisioned — and stop the 404-per-tick flood — after
      // MAX_CONSECUTIVE_404S in a row. Non-404 failures stay silent, keep
      // polling, and reset the streak.
      const saw404 = [channelsResult, dmsResult].some(
        (r) => r.status === 'rejected' && r.reason instanceof HttpError && r.reason.status === 404,
      )
      if (saw404) {
        consecutive404Ref.current += 1
        // Latch once the streak proves the feature is unprovisioned, or
        // immediately when a one-shot refocus re-probe is still 404 (bounds the
        // heal attempt to one request per tab focus).
        if (forcedReprobe || consecutive404Ref.current >= MAX_CONSECUTIVE_404S) {
          channelsUnavailableRef.current = true
          // Re-seed on any future recovery so a heal re-baselines instead of
          // toasting the whole backlog accrued while the feature was down.
          seededChannelsRef.current = false
          seededDmsRef.current = false
          dmToastAttemptsRef.current = {}
          setChannels(null)
          setDms(null)
          setTotalUnreadDMs(0)
          // Drop channel-mention toasts so the bell dot isn't held lit by a now
          // dead poller (DM unread is already zeroed above).
          setToasts((prev) => prev.filter((t) => t.type !== 'channel'))
          return
        }
      } else {
        consecutive404Ref.current = 0
      }

      const nextChannels = channelsResult.status === 'fulfilled' ? channelsResult.value : null
      const nextDms = dmsResult.status === 'fulfilled' ? dmsResult.value : null
      if (!nextChannels && !nextDms) return
      // Any fulfilled list disproves "not provisioned" (a 404 on a latched
      // re-probe already returned above; a non-404 failure doesn't refute
      // availability), so clear the latch even on partial success — otherwise a
      // refocus re-probe that publishes one list while the other endpoint has a
      // transient non-404 failure would leave a frozen, never-updating list
      // behind a still-latched (dead) poller.
      channelsUnavailableRef.current = false

      // Publish the freshest lists for context consumers (e.g. SidebarRail),
      // keeping the previous array identity when nothing consumers render has
      // changed so the context memo below actually prevents per-tick re-renders.
      if (nextChannels) {
        // A poll racing an in-flight markChannelRead must not republish the
        // server's still-stale unread count over the optimistic clear — clamp
        // those channels to 0 until the read settles or the server catches up.
        const inFlightReads = inFlightReadsRef.current
        const publishableChannels =
          inFlightReads.size === 0
            ? nextChannels
            : nextChannels.map((ch) =>
                inFlightReads.has(ch.id) && (ch.unreadCount ?? 0) > 0
                  ? { ...ch, unreadCount: 0 }
                  : ch,
              )
        setChannels((prev) =>
          sameChannelList(prev, publishableChannels) ? prev : publishableChannels,
        )
      }
      if (nextDms) setDms((prev) => (sameChannelList(prev, nextDms) ? prev : nextDms))

      // Seed each list's delta baseline independently on its first successful
      // fetch. A coupled both-or-nothing seed would let a channels endpoint
      // that fails from session start publish DM badges above while blocking
      // the DM bell/toasts (and vice versa) until it recovered.
      let channelsFirstSeen = false
      if (nextChannels && !seededChannelsRef.current) {
        channelsFirstSeen = true
        seededChannelsRef.current = true
        for (const ch of nextChannels) prevCountsRef.current[ch.id] = ch.unreadCount ?? 0
      }
      let dmsFirstSeen = false
      if (nextDms && !seededDmsRef.current) {
        dmsFirstSeen = true
        seededDmsRef.current = true
        for (const d of nextDms as Channel[]) prevCountsRef.current[d.id] = d.unreadCount ?? 0
      }

      const activeChannelId = localStorage.getItem(`xavior:lastChannel:${workspaceId}`)
      const activeDmUserId = localStorage.getItem(`xavior:lastDmUser:${workspaceId}`)

      // Returns true while the DM still has toast-retry budget; at the cap it
      // clears the counter and the caller advances the baseline instead of
      // re-fetching the same DM every tick forever.
      const underDmToastCap = (id: string) => {
        const attempts = (dmToastAttemptsRef.current[id] ?? 0) + 1
        if (attempts >= MAX_DM_TOAST_ATTEMPTS) {
          delete dmToastAttemptsRef.current[id]
          return false
        }
        dmToastAttemptsRef.current[id] = attempts
        return true
      }

      // DM deltas — always toast (skip the seed tick: baseline just captured)
      if (nextDms && !dmsFirstSeen) {
        for (const dm of detectUnreadDeltas(prevCountsRef.current, nextDms as Channel[])) {
          const otherId = dm.name.split('+').find((id) => id !== userId) ?? ''
          if (otherId && otherId === activeDmUserId) {
            // Confirmed no toast needed (that DM is open) — safe to advance.
            prevCountsRef.current[dm.id] = dm.unreadCount ?? 0
            continue
          }
          try {
            const msgs = await listMessages(workspaceId, dm.id, { limit: 5 }, token)
            if (epoch !== pollEpochRef.current) return
            const last = msgs.filter((m) => m.userId !== userId).at(-1)
            if (last) {
              addToastRef.current({
                id: `${dm.id}-${last.id}`,
                channelId: dm.id,
                type: 'dm',
                senderName: last.userName ?? 'Someone',
                preview: last.content ?? '',
                channelName: '',
                otherUserId: otherId,
              })
              // Advance the baseline only once the toast actually fired, so a
              // missed toast (message outside the fetch window, or a failed
              // fetch) re-fires on the next poll instead of being lost.
              prevCountsRef.current[dm.id] = dm.unreadCount ?? 0
              delete dmToastAttemptsRef.current[dm.id]
            } else if (!underDmToastCap(dm.id)) {
              // The window keeps missing an other-user message — give up so
              // this DM isn't re-fetched every tick for the rest of the session.
              prevCountsRef.current[dm.id] = dm.unreadCount ?? 0
            }
          } catch {
            // Silent — baseline untouched so the toast re-fires, but bounded:
            // a persistently failing fetch (e.g. 403 on a departed DM) advances
            // the baseline after MAX_DM_TOAST_ATTEMPTS instead of retrying forever.
            if (!underDmToastCap(dm.id)) prevCountsRef.current[dm.id] = dm.unreadCount ?? 0
          }
          // listMessages awaited above — bail if the session moved mid-loop so a
          // stale toast/baseline write can't leak into the new workspace.
          if (epoch !== pollEpochRef.current) return
        }
      }

      // Channel (group) deltas — toast only on @mention (skip the seed tick)
      if (nextChannels && !channelsFirstSeen) {
        for (const ch of detectUnreadDeltas(prevCountsRef.current, nextChannels)) {
          // A poll can race a just-issued markChannelRead: the server may still
          // report the old unread count before the read POST lands, which would
          // re-toast the channel the user just opened. Skip channels with an
          // in-flight read; the optimistic clear already zeroed prevCountsRef, so
          // the next poll after settle sees no delta.
          if (inFlightReadsRef.current.has(ch.id)) continue
          prevCountsRef.current[ch.id] = ch.unreadCount ?? 0
          if (ch.id === activeChannelId) continue
          try {
            const msgs = await listMessages(workspaceId, ch.id, { limit: 5 }, token)
            // The session moved while we awaited — don't toast the previous
            // workspace's mention into the new one (mirrors the DM loop's
            // check-before-toast ordering).
            if (epoch !== pollEpochRef.current) return
            const newMsgs = msgs.filter((m) => m.userId !== userId)
            const mentioned = hasMentionOf(newMsgs, displayNameRef.current, userId)
            if (mentioned) {
              addToastRef.current({
                id: `${ch.id}-${mentioned.id}`,
                channelId: ch.id,
                type: 'channel',
                senderName: mentioned.userName ?? 'Someone',
                preview: mentioned.content ?? '',
                channelName: ch.name,
              })
            }
          } catch { /* silent */ }
          if (epoch !== pollEpochRef.current) return
        }
      }

      if (nextDms) setTotalUnreadDMs(nextDms.reduce((sum, d) => sum + (d.unreadCount ?? 0), 0))
    } catch {
      // List failures are handled via Promise.allSettled above and the per-DM/
      // channel fetches carry their own catch — this only guards truly
      // unexpected throws so the interval's void poll() never rejects.
    } finally {
      // Release the guard only if we still own the in-flight slot: our epoch is
      // current AND no newer poll superseded us via the stale-timeout takeover.
      // A superseded hung poll that later settles must not free a running poll's
      // guard.
      if (epoch === pollEpochRef.current && pollStartedAtRef.current === startedAt) {
        pollInFlightRef.current = false
        pollStartedAtRef.current = 0
      }
    }
  }, [workspaceId, token, userId])

  useEffect(() => {
    if (!workspaceId || !token) return
    // Invalidate any poll still in flight from the previous workspace/token so
    // its settle handlers (catch/finally/state writes) become no-ops.
    pollEpochRef.current += 1
    seededChannelsRef.current = false
    seededDmsRef.current = false
    dmToastAttemptsRef.current = {}
    channelsUnavailableRef.current = false
    forceReprobeRef.current = false
    consecutive404Ref.current = 0
    pollInFlightRef.current = false
    pollStartedAtRef.current = 0
    prevCountsRef.current = {}
    setTotalUnreadDMs(0)
    setToasts([])
    setChannels(null)
    setDms(null)
    void poll()
    pollRef.current = setInterval(poll, POLL_INTERVAL)
    const onVisible = () => {
      if (document.hidden) return
      // Tab refocus: allow one probe past a not-provisioned latch so a
      // solo→team upgrade (same workspace/token, no effect re-run) self-heals.
      if (channelsUnavailableRef.current) {
        consecutive404Ref.current = 0
        forceReprobeRef.current = true
      }
      void poll()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [workspaceId, token, poll])

  // Optimistically clear a channel's unread BADGE in the shared `channels` list,
  // persist the read server-side (rolled back on failure), and zero the delta
  // baseline so the next poll doesn't re-toast. Re-homed from SidebarRail so the
  // single owner of channel/DM state clears the rail badge. Does NOT touch the
  // bell dot (DM unread + live mention toasts) — that behavior is unchanged.
  const markChannelReadOptimistic = useCallback(
    (channelId: string) => {
      if (!workspaceId || !token) return
      let prevCount: number | undefined
      setChannels((prev) => {
        if (!prev) return prev
        return prev.map((ch) => {
          if (ch.id !== channelId) return ch
          prevCount = ch.unreadCount
          return { ...ch, unreadCount: 0 }
        })
      })
      // Keep the delta baseline in sync so the next poll doesn't re-toast a count
      // that hasn't actually changed server-side.
      prevCountsRef.current[channelId] = 0
      // Guard the read-vs-poll race until the POST settles (see poll's channel
      // delta loop).
      inFlightReadsRef.current.add(channelId)
      markChannelRead(workspaceId, channelId, token)
        .catch(() => {
          if (prevCount !== undefined && prevCount > 0) {
            setChannels((prev) =>
              prev
                ? prev.map((ch) => (ch.id === channelId ? { ...ch, unreadCount: prevCount } : ch))
                : prev,
            )
            prevCountsRef.current[channelId] = prevCount
          }
        })
        .finally(() => {
          inFlightReadsRef.current.delete(channelId)
        })
    },
    [workspaceId, token],
  )

  // Mention indicator is derived from live mention toasts, not a separate
  // counter: addToast dedups by channelId, so a counter would drift above the
  // toast count and leave the bell dot stuck on. DM unread stays server-truth.
  const hasUnread = totalUnreadDMs > 0 || toasts.some((t) => t.type === 'channel')

  // Stable context value — channels/dms change every 3s poll, so an inline object
  // literal would re-render every useNotifications() consumer (incl. the heavy
  // SidebarRail) on each tick. Memoize so consumers re-render only on real change.
  const contextValue = useMemo(
    () => ({ hasUnread, channels, dms, markChannelRead: markChannelReadOptimistic }),
    [hasUnread, channels, dms, markChannelReadOptimistic],
  )

  return (
    <NotificationsContext.Provider value={contextValue}>
      {children}
      <NotificationToastStack toasts={toasts} onDismiss={removeToast} onActivate={handleActivate} />
    </NotificationsContext.Provider>
  )
}
